<template>
  <div>
    <b-row>
      <b-col>
        <b-form-group class="mb-2">
          <label>{{$t("views.agreements.form.contractor")}}: </label>
          <base-select-input
            required
            v-model="data.idClient"
            :search-callback="userDataSearchCallback"
            :query-param="'userdata'"
            :display-values="['fullName', 'firmname1']"
            :is-auto-complete-mode="$store.getters['configuration/enableAutoCompleteForUserData']"
            @input="clearAddresses()"
          ></base-select-input>
          <div v-if="isInvalid('idClient')" class="text-danger">
            {{ violations.idClient }}
          </div>
        </b-form-group>
        <b-btn
          size="sm"
          v-if="data.idClient"
          @click="toggleUpdateUserDataModal()"
          variant="info"
        >
          <i class="fal fa-edit fa-lg"></i>
          {{$t('views.agreements.form.editContractor')}}
        </b-btn>
        <b-btn
          size="sm"
          v-if="(data.agreementStatus === '1' || data.agreementStatus === '2' || data.id === undefined)"
          @click="toggleCreateUserDataModal()"
        >
          <i class="fal fa-plus fa-lg"></i>
          {{$t('views.agreements.form.addContractor')}}
        </b-btn>
        <div v-if="data.idClient && !isLoadingUserData">
          <hr class="my-1">
          <b-row v-for="(address, addressIndex) in data.agreementAddresses" :key="'address_'+addressIndex">
            <b-col class="align-self-center mt-3" cols="1">
              <button
                type="button"
                class="btn btn-link fa fa-trash fa-lg"
                @click="removeAddress(addressIndex)"
                v-b-tooltip :title="$t('views.userdata.form.deleteAddress')">
              </button>
            </b-col>
            <b-col cols="11">
              <b-form-group>
                <label
                  :for="'agreement_address_type_'+addressIndex"
                  class="form-control-label">{{$t('views.userdata.form.addressType')}}:</label>
                <model-select
                  v-model="data.agreementAddresses[addressIndex].addressType"
                  :options="filteredAddressTypeOptions"
                  @input="assignSelectedAddressToForm($event, addressIndex)"
                  class="mt-1"
                ></model-select>
              </b-form-group>
            </b-col>
          </b-row>
          <div>
            <button
              type="button"
              class="btn btn-info btn-sm"
              @click="addNewAddress()">
              <i class="fal fa-edit fa-lg"></i>
              {{ $t('buttons.addAddressToContractor') }}
            </button>
            <button
              type="button"
              class="btn btn-sm"
              @click="addAddress()">
              <i class="fal fa-plus fa-lg"></i>
              {{ $t('buttons.addAddressToAgreement') }}
            </button>
          </div>
        </div>
      </b-col>
      <b-col cols="6" v-if="isLoadingUserData">
        <Loader></Loader>
      </b-col>
      <b-col v-else-if="data.idClient && !isLoadingUserData">
          <p class="h6">{{$t("views.agreements.form.contractor")}}: </p>
          <p v-if="retrieved.name" ><span>{{$t('views.agreements.show.nameAndSurname')}}</span>: <b>{{retrieved.fullName}} </b></p>
          <p v-else-if="retrieved.firmname1" ><span>{{$t('views.agreements.show.firmname')}}</span>: <b>{{ retrieved.firmname1 }} </b></p>
          <p v-if="retrieved.userEmail"><span>{{$t('views.agreements.show.email')}}</span>: <b><a :href="'mailto: ' + retrieved.userEmail">{{ retrieved.userEmail }}</a></b></p>
          <p v-if="retrieved.mobilePhone"><span>{{$t('views.agreements.show.mobilePhone')}}</span>: <b><phoneNumberLink :phoneNumber="retrieved.mobilePhone"/></b></p>
          <p v-if="retrieved.street || retrieved.postcode || retrieved.city"><span><b>{{$t('views.agreements.show.address')}}:</b></span></p>
          <p v-if="retrieved.street"><span>{{$t('views.agreements.show.street')}}</span>: <b>{{retrieved.street}}</b></p>
          <p v-if="retrieved.postcode || retrieved.city"><span>{{$t('views.agreements.show.postcode')}}, {{$t('views.agreements.show.city')}}</span>: <b>{{retrieved.postcode}} {{retrieved.city}}</b></p>
        <b-row v-for="(address, addressIndex) in data.agreementAddresses" :key="'output_address_'+addressIndex">
          <b-col v-if="address.addressType">
            <p><b>{{ $t('views.agreements.show.address') + ' - ' + addressTypeOptions[address.addressType - 1].text }}:</b></p>
            <p> {{ address.street }}, </p>
            <p> {{ address.city + ' ' + address.postcode }} </p>
          </b-col>
        </b-row>
      </b-col>
      <create-modal></create-modal>
      <update-modal :id-user-data="idUserData"></update-modal>
    </b-row>
    <CreateUserDataAddressModal></CreateUserDataAddressModal>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { ModelSelect } from 'vue-search-select'
import Loader from '@/components/Dashboard/Layout/Loader'
import CreateModal from '@/components/Dashboard/Views/firm/modals/CreateModal'
import UpdateModal from '@/components/Dashboard/Views/firm/modals/UpdateModal'
import addParamsToQuery from '@/utils/addParamsToQuery'
import ApiService from '@/utils/ApiService'
import getId from '@/utils/getIdFromLink'
import CreateUserDataAddressModal from '@/components/Dashboard/Views/userdataaddress/components/CreateUserDataAddressModal'
import remove from '@/utils/remove'
import PhoneNumberLink from '@/components/Dashboard/Views/firm/components/PhoneNumberLink'
import BaseSelectInput from '@/components/BaseComponents/BaseSelectInput'

export default {
  name: 'UserData',
  data () {
    return {
      idUserData: null,
      addressOptions: [],
      selectedAddressType: '',
      addressTypeOptions: this.$store.getters['configuration/addressTypeOptions']
    }
  },

  props: ['data', 'isInvalid', 'violations'],

  components: {
    PhoneNumberLink,
    ModelSelect,
    Loader,
    CreateModal,
    UpdateModal,
    CreateUserDataAddressModal,
    BaseSelectInput
  },

  computed: {
    isUpdate () {
      return this.$route.name === 'EXABAgreementsUpdate'
    },

    addressCreated () {
      return this.$store.state.modals.userdataaddress.create.created
    },

    filteredAddressTypeOptions () {
      return this.addressTypeOptions.filter(entry => {
        return this.addressOptions.some(retrievedAddress => {
          return retrievedAddress.addressType === entry.value
        })
      })
    },

    ...mapGetters({
      userDataCreated: 'userdata/create/created',
      isLoadingUserData: 'userdata/show/isLoading',
      userDataList: 'userdata/list/items',
      retrieved: 'userdata/show/retrieved',
      updated: 'userdata/update/updated'
    }),

    userDataOptions () {
      return this.userDataList.map(entry => {
        return {
          text: entry.firmname1 === '' ? entry.fullName : entry.firmname1,
          value: entry['@id']
        }
      })
    },

    isUpdateRoute () {
      return this.$route.name === 'EXABAgreementsUpdate'
    }

  },

  watch: {
    addressCreated (val) {
      if (val) this.fetchAddressesForUserData(this.idUserData)
    },

    retrieved (val) {
      if (val) this.idUserData = val['@id']
    },

    'data.idClient': {
      immediate: true,
      handler (val) {
        if (val) {
          this.fetchUserData(val)
          this.fetchAddressesForUserData(val)
        }
      }
    },

    userDataCreated (val) {
      if (val) {
        this.data.idClient = val['@id']
        this.$forceUpdate()
      }
    },

    updated (val) {
      if (val) this.fetchUserData(val['@id'])
    }
  },

  methods: {
    ...mapActions({
      fetchUserData: 'userdata/show/retrieve'
    }),

    ...mapMutations({
      toggleCreateUserDataModal: 'userdata/createModal/toggleModal',
      toggleUpdateUserDataModal: 'userdata/updateModal/toggleModal'
    }),

    clearAddresses () {
      this.data.agreementAddresses = []
    },

    addNewAddress () {
      this.$store.state.modals.userdataaddress.create.show = 1
      this.$store.state.modals.userdataaddress.create.userDataIRI = this.idUserData
    },

    removeAddress (addressIndex) {
      let currentAddress = this.data.agreementAddresses[addressIndex]
      if (currentAddress['@id']) {
        remove(currentAddress['@id'], () => {
          this.data.agreementAddresses.splice(addressIndex, 1)
          this.$forceUpdate()
        })
      } else {
        this.data.agreementAddresses.splice(addressIndex, 1)
        this.$forceUpdate()
      }
    },

    assignSelectedAddressToForm (input, addressIndex) {
      let addressObject = this.data.agreementAddresses[addressIndex]
      let selectedAddress = this.addressOptions.find(option => option.addressType === input)
      addressObject.idUserDataAddress = selectedAddress['@id']
      addressObject.city = selectedAddress.city
      addressObject.postcode = selectedAddress.postcode
      addressObject.province = selectedAddress.province
      addressObject.street = selectedAddress.street
      this.$forceUpdate()
    },

    async fetchAddressesForUserData (idUserData) {
      let link = addParamsToQuery('user_data_addresses', {
        idUserData: getId(idUserData),
        pagination: 'false'
      })
      const response = await new ApiService(link).getResults()
      this.addressOptions = response.items
    },

    addAddress () {
      this.data.agreementAddresses.push({
        idAgreement: this.isUpdate ? this.data['@id'] : null,
        addressType: ''
      })
      this.$forceUpdate()
    },
    async userDataSearchCallback (params = {}) {
      let baseParams = { pagination: false, isContractor: 1 }
      Object.assign(baseParams, params)
      const response = await new ApiService('user_datas/list', baseParams).getResults()
      return response.items
    }
  }
}
</script>
